import React, { useState } from 'react';
import { Download, Loader2, Printer } from 'lucide-react';
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceDot } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { SCENARIO_LABELS, TAX_YEAR, annualizedROI, STATE_TAX_RATES, FEIE_EXCLUSION, computeHalvingMonths } from '../../lib/constants';
import { calculateStrategy, computePlanEconomics } from '../../lib/engine';
import { ReportCover, SectionHeader, StatRow, ReportTable, Footnote, NextStep, Kicker } from '../shared/Report';
import { downloadPlanPdf } from '../../lib/api';

const axisStyle = { fontSize: 9, fill: '#737373', fontFamily: "'IBM Plex Mono', monospace" };

export default function StrategyReportSection({ printMode = false }) {
  const {
    activeStrategy,
    activeCalc,
    displayCalculations,
    miningEconomics,
    equivalentDCA,
    taxBenefits,
    timeHorizon,
    btcPrice,
    priceScenario,
    electricityCost,
    hostingFixed,
    uptimeFactor,
    effectiveIncomeTaxRate,
    stateTaxRate,
    poolFee, blockReward, difficulty, difficultyGrowth,
    startingTreasury, startingCash,
    marketDataMeta,
    taxRate, stateCode, entityType, ownerResidency, w2SSWages, otherSEIncome,
    showAfterTaxEconomics,
    advisorProfile, updateAdvisorProfile,
    planMgr,
    fmt, fBTC,
  } = useDashboard();

  if (!activeCalc || !miningEconomics) {
    return (
      <div className="text-sm italic p-4" style={{ color: '#737373' }}>
        Set up this plan's configuration to generate the report.
      </div>
    );
  }

  /* ── What this engagement is worth ──────────────────────────────────────
     No new maths. The same engine, run a second time on the same budget and
     the same hosting rate, using a previous-generation machine. The point is
     that $/TH is near-identical between generations while efficiency is not,
     so a buyer optimising on sticker price picks more machines and pays for
     it in power for five years. Verified against live vendor pricing and
     Simple Mining's published rates on 2026-08-03. */
  const ALT_HW = { label: 'Previous generation (15 J/TH)', hash: 234, watts: 3510, price: 5500 };

  const engagementValue = (() => {
    if (!activeStrategy || !activeCalc || !miningEconomics) return null;
    const budget = (activeStrategy.startingMiners || 0) * (activeStrategy.minerPrice || 0);
    if (budget <= 0) return null;
    const altCount = Math.floor(budget / ALT_HW.price);
    if (altCount < 1) return null;

    const params = {
      timeHorizon, btcPrice, priceScenario, electricityCost, hostingFixed, poolFee,
      uptimeFactor, blockReward, difficulty, difficultyGrowth,
      startingTreasury, startingCash,
      halvingMonths: computeHalvingMonths(marketDataMeta?.blockHeight),
    };
    const altStrategy = {
      ...activeStrategy,
      startingMiners: altCount,
      hashRate: ALT_HW.hash, powerDraw: ALT_HW.watts, minerPrice: ALT_HW.price,
      initialResaleValue: Math.round(ALT_HW.price * 0.11),
      expansions: [],
    };
    const thisStrategy = { ...activeStrategy, expansions: [] };
    try {
      const taxBlock = { taxRate, stateCode, entityType, ownerResidency, w2SSWages, otherSEIncome, showAfterTaxEconomics };
      const opts = { btcPrice, timeHorizon, stateTaxRates: STATE_TAX_RATES, feieExclusion: FEIE_EXCLUSION };
      const mineR = calculateStrategy(thisStrategy, params);
      const altR = calculateStrategy(altStrategy, params);
      const mineE = computePlanEconomics(mineR, taxBlock, opts);
      const altE = computePlanEconomics(altR, taxBlock, opts);
      if (!mineE || !altE) return null;
      const sum = (r) => r.annualData.reduce((a, y) => a + (y.opEx || 0), 0);
      const mineOpEx = sum(mineR), altOpEx = sum(altR);
      const fee = (mineR.totalBTCMinedGrossValue || 0) * 0.03;
      return {
        alt: ALT_HW,
        mine: { n: thisStrategy.startingMiners, th: thisStrategy.startingMiners * thisStrategy.hashRate,
                kw: thisStrategy.startingMiners * thisStrategy.powerDraw / 1000,
                opEx: mineOpEx, cost: mineE.netCostPerBTC, mult: mineE.miningMultiple },
        other: { n: altCount, th: altCount * ALT_HW.hash, kw: altCount * ALT_HW.watts / 1000,
                 opEx: altOpEx, cost: altE.netCostPerBTC, mult: altE.miningMultiple },
        delta: altOpEx - mineOpEx,
        fee,
        ratio: fee > 0 ? (altOpEx - mineOpEx) / fee : 0,
      };
    } catch { return null; }
  })();

  const strat = activeStrategy;
  const annualData = activeCalc.annualData || [];
  const totalMiners = strat.startingMiners + (strat.expansions || []).reduce((s, e) => s + (e.miners || 0), 0);
  const initialInvestment = strat.initialCapexIsSunk ? 0 : strat.startingMiners * strat.minerPrice;
  const totalCapExPaid = activeCalc.totalCapExPaid || activeCalc.totalCapEx || 0;
  // From the shared economics rather than summed here, so the headline cost, the
  // cost basis and the Mining Multiple cannot drift apart.
  const totalOpExOverHold = miningEconomics.totalOpEx
    ?? annualData.reduce((s, y) => s + (y.opEx || 0), 0);

  /* What actually happens to the mined Bitcoin.
     The report used to assert "kept, not sold" as a fixed line of copy, which is
     false for any plan that funds its own power bill — the self-funding
     configurations sell most of what they mine. An assumptions list that does
     not track the plan is worse than no list, so read it off the run. */
  const btcSoldTotal = activeCalc.totalBTCSold || 0;
  const btcMinedTotal = activeCalc.totalBTCMined || 0;
  const soldFraction = btcMinedTotal > 0 ? btcSoldTotal / btcMinedTotal : 0;
  const sellsBTC = soldFraction >= 0.005;   // below this it is rounding, not a policy
  const soldPct = Math.round(soldFraction * 100);
  const btcDisposition = sellsBTC
    ? `${soldPct}% of mined Bitcoin sold to cover costs, ${100 - soldPct}% kept`
    : 'Mined Bitcoin is kept, not sold';
  const scenarioLabel = SCENARIO_LABELS[priceScenario] || '';
  const combinedRate = (effectiveIncomeTaxRate + stateTaxRate) / 100;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  /* The plan's name, not the configuration's.
     Since one plan holds one configuration, the strategy inside still carries
     whatever it was called under the old model, and the two drift: a plan named
     "Hydro Miners - Funded by Other Business" reported itself as "Self-Funding"
     on its own cover, because this read the strategy. The plan is what the
     client picked and what the file is named after, so it wins. Falls back to
     the strategy name when no plan is open, which is the local-only case.
     Leading ordinals are stripped either way, left over from when
     configurations were numbered. */
  const planLabel = (planMgr?.activePlan?.name || strat.name || 'Plan')
    .replace(/^\d+\.\s*/, '');
  const clientName = planMgr?.clients?.find(c => c.id === planMgr.activeClientId)?.display_name
    || planMgr?.clients?.find(c => c.id === planMgr.activeClientId)?.username
    || 'Client';

  /* ── Headline outcomes ── */
  const finalROI = activeCalc.finalROI || 0;
  const roiPerYear = annualizedROI(finalROI, timeHorizon);
  const mult = miningEconomics.miningMultiple ?? (activeCalc.miningMultiplier || 0);
  const grossMult = miningEconomics.grossMultiple ?? mult;
  const hasTaxOnMult = (miningEconomics.taxSavings || 0) > 0 && mult !== grossMult;

  /* ── 01 Production ──
     Tables stop at the last year with activity; trailing retired-fleet
     years add rows of zeros without information. */
  const lastActiveYear = annualData.reduce((last, y) =>
    ((y.btcMined || 0) > 0 || (y.opEx || 0) > 0 || (y.capexThisYear || 0) > 0) ? y.year : last, 1);
  const activeYears = annualData.filter(y => y.year <= lastActiveYear);
  const yearsTrimmed = annualData.length - activeYears.length;
  const trimNote = yearsTrimmed > 0
    ? ` Years ${lastActiveYear + 1}-${timeHorizon} are omitted: the fleet is fully retired, with no production or cost. Held BTC continues to appreciate through year ${timeHorizon}.`
    : '';
  let cumBTC = 0;
  const prodRows = activeYears.map(y => {
    cumBTC += y.btcMined || 0;
    return { year: `Year ${y.year}`, btc: fBTC(y.btcMined || 0, 3), cum: fBTC(cumBTC, 3), price: fmt(y.btcPrice || 0), value: fmt(y.btcMinedValue || 0) };
  });
  const totalBTCMined = activeCalc.totalBTCMined || 0;
  const prodStats = activeYears.slice(0, 5).map(y => ({ value: fBTC(y.btcMined || 0, 3), label: `Year ${y.year}`, accent: '#92700C' }));

  // Defined before its first use: the deployment schedule below dates each
  // expansion, and this used to sit further down the file.
  const monthToDate = (mth) => mth
    ? new Date(new Date().setMonth(new Date().getMonth() + mth)).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;

  /* ── Deployment schedule ──────────────────────────────────────────────────
     Reads the same two sources the engine builds its cohorts from: the starting
     fleet, then each expansion. Anything here that disagrees with the engine
     would mean the report is describing a fleet that was not simulated. */
  const deployments = [
    ...(strat.startingMiners > 0 ? [{
      label: 'Initial fleet',
      month: 1,
      miners: strat.startingMiners,
      hashRate: strat.hashRate,
      powerDraw: strat.powerDraw,
      minerPrice: strat.minerPrice,
      lifespan: strat.initialMinerLifespan,
      resaleValue: strat.initialResaleValue,
    }] : []),
    ...(strat.expansions || [])
      .filter(e => e.miners > 0)
      .sort((a, b) => a.month - b.month)
      .map((e, i) => ({
        label: `Expansion ${i + 1}`,
        month: e.month,
        miners: e.miners,
        hashRate: e.hashRate,
        powerDraw: e.powerDraw,
        minerPrice: e.minerPrice,
        lifespan: e.lifespan,
        resaleValue: e.resaleValue,
      })),
  ];

  // Watts per TH/s is joules per terahash — the number that decides whether a
  // machine survives the next difficulty rise, and the one buyers skip past
  // because sticker price is easier to compare.
  const jPerTH = (w, th) => (th > 0 ? w / th : 0);

  /* Machines leave as well as arrive. The engine retires a cohort in the month
     its months-in-service first exceeds its lifespan — startMonth + lifespan —
     and credits count x resaleValue that month. Showing only purchases made the
     fleet look permanent and left the resale proceeds, which are real money back
     and a taxable event, out of the schedule entirely. */
  const horizonMonths = timeHorizon * 12;
  const fleetEvents = [
    ...deployments.map(d => ({ ...d, kind: 'deploy', at: d.month })),
    ...deployments
      .filter(d => d.lifespan > 0 && d.month + d.lifespan <= horizonMonths)
      .map(d => ({ ...d, kind: 'retire', at: d.month + d.lifespan })),
  ].sort((a, b) => a.at - b.at || (a.kind === 'deploy' ? -1 : 1));

  let runningFleet = 0;
  const deployRows = fleetEvents.map(ev => {
    const isRetire = ev.kind === 'retire';
    runningFleet += isRetire ? -ev.miners : ev.miners;
    const unitValue = isRetire ? (ev.resaleValue || 0) : ev.minerPrice;
    const cash = ev.miners * unitValue;
    return {
      what: isRetire ? `${ev.label} retires` : ev.label,
      online: ev.at === 1 ? 'At start' : `Mo ${ev.at} · ${monthToDate(ev.at)}`,
      miners: isRetire ? `-${ev.miners}` : `+${ev.miners}`,
      fleet: `${runningFleet}`,
      spec: isRetire ? '—' : `${ev.hashRate} TH/s · ${jPerTH(ev.powerDraw, ev.hashRate).toFixed(1)} J/TH`,
      // Exact, not abbreviated: "$5.5K" and "$5.8K" are the same number to a
      // reader skimming, and unit price is what gets checked against a quote.
      unit: `$${Math.round(unitValue).toLocaleString()}`,
      capital: isRetire ? `(${fmt(cash)})` : fmt(cash),
      _muted: isRetire,
    };
  });
  const totalResale = fleetEvents
    .filter(e => e.kind === 'retire')
    .reduce((s, e) => s + e.miners * (e.resaleValue || 0), 0);
  const fleetHash = deployments.reduce((s, d) => s + d.miners * d.hashRate, 0);
  const fleetWatts = deployments.reduce((s, d) => s + d.miners * d.powerDraw, 0);
  const fleetCapital = deployments.reduce((s, d) => s + d.miners * d.minerPrice, 0);
  const fleetMiners = deployments.reduce((s, d) => s + d.miners, 0);
  const costPerTH = fleetHash > 0 ? fleetCapital / fleetHash : 0;
  // Continuous draw, so the monthly power bill is a fixed known quantity before
  // any Bitcoin is mined. Worth stating plainly: it is the commitment being made.
  const monthlyPowerCost = (fleetWatts / 1000) * 24 * 30.4 * electricityCost;

  /* ── 02 Expenses & tax ── */
  const depByYearIdx = {};
  (taxBenefits?.yearlyBreakdown || []).forEach(b => { depByYearIdx[b.year - TAX_YEAR] = b.depreciation; });
  let totalDeductions = 0, totalOffset = 0, totalOpExAll = 0;
  const taxRows = activeYears.map((y, i) => {
    const dep = depByYearIdx[i] || 0;
    const deductions = (y.opEx || 0) + dep;
    const offset = deductions * combinedRate;
    totalDeductions += deductions; totalOffset += offset; totalOpExAll += y.opEx || 0;
    return { year: `Year ${y.year}`, opEx: fmt(y.opEx || 0), dep: dep > 0 ? fmt(dep) : '—', ded: fmt(deductions), offset: fmt(offset) };
  });
  const avgMonthlyOpEx = annualData.length ? totalOpExAll / (annualData.length * 12) : 0;

  /* ── 03 Cost basis ── */
  let cumInvested = 0, cumMined = 0;
  const basisRows = [];
  const basisChart = [];
  activeYears.forEach(y => {
    cumInvested += (y.opEx || 0) + (y.capexThisYear || 0);
    cumMined += y.btcMined || 0;
    const basis = cumMined > 0 ? cumInvested / cumMined : 0;
    basisRows.push({ year: `Year ${y.year}`, invested: fmt(cumInvested), btc: fBTC(cumMined, 3), basis: basis > 0 ? fmt(basis) : '—', price: fmt(y.btcPrice || 0) });
    basisChart.push({ year: `Yr ${y.year}`, basis: basis > 0 ? basis : null, price: y.btcPrice || 0 });
  });
  const finalBasis = cumMined > 0 ? cumInvested / cumMined : 0;

  /* ── 04 ROI ── */
  const md = activeCalc.monthlyData || [];
  let cumExp = initialInvestment, cumRev = 0;
  /* Break-even series. Three lines: cumulative value of Bitcoin mined, gross
     cumulative expenses, and expenses net of write-offs. The gap between the
     two expense lines is the deduction, and the two crossings are the pre-tax
     and post-tax break-even months. Crossings are read off THIS series rather
     than taken from breakEvenMonth (which is NAV-based) so the marked point
     always sits where the reader can see the lines cross. */
  const savingsByMonth = new Map(
    (miningEconomics?.cumulativeTaxSavingsByMonth || []).map(x => [x.month, x.cumulativeTaxSavings])
  );
  const hasSavings = savingsByMonth.size > 0;
  const fullSeries = md.map(d => {
    // capexPlacedInService includes the starting fleet in month 1; monthCapex
    // does not, which left the hardware purchase out of this line entirely and
    // made the chart cross at month 1 on any single-tranche plan.
    cumExp += (d.capexPlacedInService ?? d.monthCapex ?? 0) + (d.totalOpEx || 0);
    cumRev += d.btcMinedGrossValue || 0;
    const saved = savingsByMonth.get(d.month) || 0;
    return { month: d.month, expenses: cumExp, netExpenses: Math.max(0, cumExp - saved), revenue: cumRev };
  });
  const crossing = (key) => {
    const hit = fullSeries.find(p => p[key] > 0 && p.revenue >= p[key]);
    return hit ? hit.month : null;
  };
  const breakEvenGross = crossing('expenses');
  const breakEvenNet = hasSavings ? crossing('netExpenses') : null;
  const roiChart = fullSeries.filter((_, i) => i % 3 === 0 || i === fullSeries.length - 1);

  /* X-axis year marks.
     Ticks have to be chosen from months the chart actually plots. Thinning to
     every third point leaves months 1, 4, 7, 10 ... — none of which is divisible
     by 24, so a formatter testing `month % 24 === 0` silently labelled nothing.
     Pick the nearest plotted month to each year boundary instead, which holds
     whatever the thinning interval or horizon happens to be. */
  const chartMonths = roiChart.map(p => p.month);
  const lastChartMonth = chartMonths.length ? chartMonths[chartMonths.length - 1] : 0;
  // Every year is too crowded past about eight; every two years stays readable.
  const tickStep = lastChartMonth > 96 ? 24 : 12;
  const yearTicks = [];
  for (let m = tickStep; m <= lastChartMonth; m += tickStep) {
    const nearest = chartMonths.reduce(
      (best, cur) => (Math.abs(cur - m) < Math.abs(best - m) ? cur : best),
      chartMonths[0]);
    if (!yearTicks.includes(nearest)) yearTicks.push(nearest);
  }
  const breakEvenShown = breakEvenNet ?? breakEvenGross;
  const breakEvenPoint = breakEvenShown
    ? fullSeries.find(p => p.month === breakEvenShown)
    : null;

  /* ── 05 Comparison ── */
  const compRows = displayCalculations.map(calc => {
    const r = calc.results;
    const mined = r.totalBTCMined || 0;
    const opEx = (calc.strategy.isBenchmark ? 0 : (r.annualData || []).reduce((s, y) => s + (y.opEx || 0), 0));
    const capEx = r.totalCapExPaid || r.totalCapEx || 0;
    const base = capEx + opEx;
    const costBTC = mined > 0 ? base / mined : (r.btcPurchased > 0 ? (r.totalInvested || capEx) / r.btcPurchased : 0);
    return {
      name: calc.strategy.id === strat.id ? planLabel : calc.strategy.name.replace(/^\d+\.\s*/, ''),
      capEx: fmt(capEx), opEx: fmt(opEx),
      btc: fBTC(mined || r.btcPurchased || 0, 2), cost: costBTC > 0 ? fmt(costBTC) : '—',
      nav: fmt(r.finalNAV || 0), roi: `${(r.finalROI || 0).toFixed(0)}%`,
      _strong: calc.strategy.id === strat.id,
    };
  });

  /* Comparison headline cards, derived from compRows' own source so the two
     cannot drift. Mining is gold; the alternatives are neutral, because they are
     reference points rather than a good/bad judgement. */
  const compStats = displayCalculations.slice(0, 3).map(calc => {
    const r = calc.results;
    const isMine = calc.strategy.id === strat.id;
    const held = r.totalBTCKept ?? r.btcPurchased ?? 0;
    return {
      value: fmt(r.finalNAV || 0),
      label: isMine ? 'Mining' : calc.strategy.name.replace(/^\d+\.\s*/, ''),
      sub: held > 0 ? `${fBTC(held, 3)} BTC held` : 'no deductions',
      accent: isMine ? '#92700C' : '#D4D4D4',
    };
  });

  const advisorField = (key, label, width) => (
    <div key={key} style={{ flex: width || 1, minWidth: 120 }}>
      <label className="block mb-1" style={{ fontSize: 9, color: '#737373', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'IBM Plex Mono', monospace" }}>{label}</label>
      <input type="text" value={advisorProfile[key] || ''} onChange={e => updateAdvisorProfile({ [key]: e.target.value })}
        className="w-full px-2 py-1.5 text-sm" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4, color: '#1A1A1A' }} />
    </div>
  );

  /* The PDF is rendered server-side from this same report, so what downloads is
     what is on screen. It needs a saved plan because the renderer fetches by id
     rather than being handed the unsaved state in the browser.
     A small server can fail to start Chromium; that returns 503 with a message
     and the Print button stays as the fallback. */
  const [pdfState, setPdfState] = useState('idle'); // idle | working
  const [pdfError, setPdfError] = useState(null);
  const activePlanId = planMgr?.activePlan?.id || null;

  const handleDownloadPdf = async () => {
    if (!activePlanId || pdfState === 'working') return;
    setPdfState('working');
    setPdfError(null);
    try {
      // The server renders from the stored plan, not from this browser, so any
      // edit still sitting behind the autosave debounce would be missing from
      // the file — a horizon changed a second ago would print at its old value.
      const flushed = await planMgr.flushSave?.();
      if (flushed && !flushed.saved && flushed.reason === 'readonly') {
        setPdfError('This is the benchmark plan, so your changes were not saved to it. The PDF shows the saved version. Create your own option to include edits.');
      }
      const result = await downloadPlanPdf(activePlanId, planMgr.activePlan?.name);
      if (!result.ok) setPdfError(result.message);
    } catch (e) {
      setPdfError('Could not reach the server. Use Print instead.');
    } finally {
      setPdfState('idle');
    }
  };

  const sec = { padding: '26px 40px 6px' };

  return (
    <div className="space-y-4">
      {!printMode && (
      <div className="no-print">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-bold text-lg" style={{ color: '#1A1A1A' }}>Strategy Report</h2>
            <p className="text-sm" style={{ color: '#737373' }}>Print-ready pro forma for client handoff.</p>
          </div>
          <div className="flex items-center gap-2">
            {pdfError && (
              <span className="text-xs" style={{ color: '#B45309', maxWidth: 260, textAlign: 'right' }}>{pdfError}</span>
            )}
            <button onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2.5 rounded font-medium text-sm"
              style={{ background: '#FFFFFF', color: '#1A1A1A', border: '1px solid #D4D4D4', cursor: 'pointer' }}>
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={handleDownloadPdf} disabled={pdfState === 'working' || !activePlanId}
              title={activePlanId ? 'Download a PDF of this report' : 'Save this plan first'}
              className="flex items-center gap-2 px-5 py-2.5 rounded font-medium text-sm"
              style={{
                background: activePlanId ? '#1A1A1A' : '#E5E5E5',
                color: activePlanId ? '#FFFFFF' : '#A3A3A3',
                border: 'none',
                cursor: pdfState === 'working' || !activePlanId ? 'default' : 'pointer',
                opacity: pdfState === 'working' ? 0.7 : 1,
              }}>
              {pdfState === 'working'
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing PDF...</>
                : <><Download className="w-4 h-4" /> Download PDF</>}
            </button>
          </div>
        </div>
        <div className="p-4 rounded" style={{ background: '#FFFFFF', border: '1px solid #E5E5E5' }}>
          <Kicker style={{ marginBottom: 8 }}>Report identity</Kicker>
          <div className="flex gap-3 flex-wrap">
            {advisorField('firm', 'Firm')}
            {advisorField('name', 'Your name')}
            {advisorField('title', 'Title')}
            {advisorField('email', 'Email')}
            {advisorField('phone', 'Phone')}
          </div>
        </div>
      </div>
      )}

      <div className="print-deliverable" style={{ background: '#FFFFFF', borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E5E5' }}>

        <ReportCover
          reportKicker={`${timeHorizon}-year pro forma`}
          reportTitle="Bitcoin mining pro forma"
          subtitle={`A year-by-year projection of Bitcoin production, operating expenses, tax position, and cost basis for the "${planLabel}" plan across a ${timeHorizon}-year hold.`}
          preparedFor={clientName}
          advisor={advisorProfile}
          date={today}
        />

        {/* ── Overview ── */}
        <div className="report-section print-flow" style={sec}>
          <SectionHeader
            kicker="Overview"
            title="Projections for your consideration"
            intro={`All figures are projections from current market data under the ${scenarioLabel} price scenario, with network difficulty assumed to grow ${(difficultyGrowth * 100).toFixed(0)}% a year. What follows covers Bitcoin production, expenses and tax position, the cost basis of Bitcoin acquired, and return on investment. Projections are estimates, not financial advice.`}
          />
          <StatRow stats={[
            // Hardware alone understated what the plan costs and mislabelled the
            // period: capital goes in over the deployment window, not the whole
            // hold. Power and hosting are the larger number over five years and
            // are what the deductions are claimed against, so both belong here.
            // Same total the cost basis and the Mining Multiple are computed on.
            { value: fmt(totalCapExPaid + totalOpExOverHold), label: 'Total cost',
              sub: `CapEx ${fmt(totalCapExPaid)} · OpEx ${fmt(totalOpExOverHold)}` },
            { value: `${totalMiners}`, label: 'Machines', sub: `starting fleet ${strat.startingMiners} · ${strat.hashRate} TH/s each` },
            { value: fmt(activeCalc.finalNAV || 0), label: 'Projected NAV', sub: `Year ${timeHorizon}` },
            { value: `${finalROI.toFixed(0)}%`, label: 'Total ROI', sub: roiPerYear !== null ? `about ${roiPerYear.toFixed(0)}%/yr · ${scenarioLabel}` : scenarioLabel, color: '#92700C' },
          ]} />
        </div>

        {/* ── Deployment ── */}
        {deployments.length > 0 && (
          <div className="report-section print-flow" style={sec}>
            <SectionHeader kicker="Hardware" title="Machines and deployment schedule"
              intro="Efficiency, stated here in joules per terahash, is what decides how long a machine keeps earning as network difficulty rises. Two machines can cost nearly the same per terahash and still differ sharply in power draw, and that gap is paid every month of the hold rather than once at purchase. The schedule below lists each tranche as it comes online, the machines retired at end of life, and what they return when sold." />
            <StatRow stats={[
              { value: `${fleetHash.toLocaleString()}`, label: 'Total hash rate', sub: `TH/s across ${fleetMiners} machine${fleetMiners === 1 ? '' : 's'}` },
              { value: `${(fleetWatts / 1000).toFixed(1)} kW`, label: 'Total draw', sub: `about ${fmt(monthlyPowerCost)}/mo power at ${'$'}${electricityCost}/kWh` },
              { value: `${jPerTH(fleetWatts, fleetHash).toFixed(1)}`, label: 'Fleet efficiency', sub: 'J/TH, lower is better', accent: '#92700C' },
              { value: `$${costPerTH.toFixed(2)}`, label: 'Cost per TH/s', sub: `${fmt(fleetCapital)} hardware` },
            ]} />
            <ReportTable
              columns={[
                { key: 'what', label: 'Event' },
                { key: 'online', label: 'Date' },
                { key: 'miners', label: 'Machines', align: 'right' },
                { key: 'fleet', label: 'Fleet', align: 'right' },
                { key: 'spec', label: 'Each', align: 'right' },
                { key: 'unit', label: 'Unit', align: 'right' },
                { key: 'capital', label: 'Cash', align: 'right' },
              ]}
              rows={deployRows}
              totalRow={{
                what: 'Net', online: '—', miners: `${fleetMiners}`,
                fleet: `${runningFleet}`,
                spec: `${fleetHash.toLocaleString()} TH/s`,
                unit: '—', capital: fmt(fleetCapital - totalResale),
              }}
              footnote={`Hosting is ${'$'}${hostingFixed} per machine per month at ${'$'}${electricityCost}/kWh. ${totalResale > 0 ? `Machines run a ${strat.initialMinerLifespan}-month life and are sold at end of life; ${fmt(totalResale)} of resale proceeds are credited as each tranche retires, so net hardware cost is ${fmt(fleetCapital - totalResale)} against ${fmt(fleetCapital)} spent. Resale is a taxable disposal against the machine's remaining basis, which is nil once it has been fully written off.` : `Machines are modelled over a ${strat.initialMinerLifespan}-month life. No tranche reaches end of life inside the ${timeHorizon}-year hold, so no resale proceeds appear.`} Specifications are the machines quoted at the time of writing; equivalent hardware may be substituted at the same efficiency and price.`}
            />
          </div>
        )}

        {/* ── Production ── */}
        <div className="report-section" style={sec}>
          <SectionHeader kicker="Production" title="Annual Bitcoin production"
            intro={`How much Bitcoin the fleet is projected to mine each year of the hold. As network difficulty rises, annual production declines while the dollar value of production moves with price. Assumes ${Math.round(uptimeFactor * 100)}% uptime. Over the full ${timeHorizon} years, the fleet is projected to mine ${fBTC(totalBTCMined, 3)} BTC.`} />
          {prodStats.length > 1 && <StatRow stats={prodStats} />}
          <ReportTable
            columns={[
              { key: 'year', label: 'Year' },
              { key: 'btc', label: 'BTC mined', align: 'right' },
              { key: 'cum', label: 'Cumulative BTC', align: 'right' },
              { key: 'price', label: 'Year-end price', align: 'right' },
              { key: 'value', label: 'Value mined', align: 'right' },
            ]}
            rows={prodRows}
            totalRow={{ year: 'Total', btc: fBTC(totalBTCMined, 3), cum: fBTC(totalBTCMined, 3), price: '—', value: fmt(annualData.reduce((s, y) => s + (y.btcMinedValue || 0), 0)) }}
            footnote={`Excludes any recovery of value through hardware resale.${trimNote}`}
          />
        </div>

        {/* ── Expenses & tax ── */}
        <div className="report-section" style={sec}>
          <SectionHeader kicker="Expenses and tax" title="Expenses & tax position"
            intro={`Operating the fleet carries power and hosting costs, while hardware and operating expenses are potentially deductible against mining income. Machine cost qualifies for bonus depreciation in the year placed in service. Figures assume a ${(combinedRate * 100).toFixed(0)}% combined rate; confirm your effective rate with your CPA.`} />
          <StatRow stats={[
            { value: fmt(totalCapExPaid), label: 'Hardware investment', sub: `${totalMiners}× machines` },
            { value: `${fmt(Math.round(avgMonthlyOpEx))} / mo`, label: 'Power + hosting', sub: `$${electricityCost}/kWh · $${hostingFixed}/miner hosting` },
            { value: fmt(totalOffset), label: `${timeHorizon}-yr tax offset`, sub: 'net over the hold', color: '#92700C' },
          ]} />
          <ReportTable
            columns={[
              { key: 'year', label: 'Year' },
              { key: 'opEx', label: 'Operating cost', align: 'right' },
              { key: 'dep', label: 'Machine deduction', align: 'right' },
              { key: 'ded', label: 'Total deductions', align: 'right' },
              { key: 'offset', label: 'Potential tax offset', align: 'right' },
            ]}
            rows={taxRows}
            totalRow={{ year: 'Total', opEx: fmt(totalOpExAll), dep: fmt(totalDeductions - totalOpExAll), ded: fmt(totalDeductions), offset: fmt(totalOffset) }}
            footnote="Potential tax offset assumes machine cost qualifies for bonus depreciation and operating costs are expensed normally. It does not account for tax owed on mining income itself. Simplified projection, not tax advice; consult a qualified CPA."
          />
        </div>

        {/* ── Cost basis ── */}
        <div className="report-section" style={sec}>
          <SectionHeader kicker="Cost basis" title="Cost basis of Bitcoin acquired"
            intro={`Cumulative dollars invested (capital plus operating cost) divided by cumulative Bitcoin mined. Early on, fixed capital dominates; as production accumulates, average cost basis settles near ${fmt(finalBasis)} per Bitcoin by year ${lastActiveYear}. Year-end price is shown for reference.`} />
          <div className="print-block" style={{ height: 170, margin: '4px 0 10px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={basisChart} margin={{ top: 14, right: 26, left: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#F0F0F0" />
                <XAxis dataKey="year" tick={axisStyle} axisLine={false} tickLine={false} />
                <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={v => `$${Math.round(v / 1000)}K`} width={44} />
                <Line type="monotone" dataKey="basis" stroke="#92700C" strokeWidth={2.5} dot={{ r: 2.5 }} name="Cost basis" isAnimationActive={false} />
                <Line type="monotone" dataKey="price" stroke="#A3A3A3" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Year-end price" isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <Footnote style={{ marginBottom: 8 }}>
            <span style={{ color: '#92700C', fontWeight: 600 }}>—</span> Average cost basis per Bitcoin&nbsp;&nbsp;
            <span style={{ color: '#A3A3A3', fontWeight: 600 }}>--</span> Projected year-end price
          </Footnote>
          <ReportTable
            columns={[
              { key: 'year', label: 'Year' },
              { key: 'invested', label: 'Cum. invested', align: 'right' },
              { key: 'btc', label: 'Cum. BTC', align: 'right' },
              { key: 'basis', label: 'Cost basis', align: 'right' },
              { key: 'price', label: 'Year-end price', align: 'right' },
            ]}
            rows={basisRows}
          />
        </div>

        {/* ── ROI ── */}
        <div className="report-section" style={sec}>
          <SectionHeader kicker="Return" title="Break-even"
            intro={`Total expenditure against the cumulative value of Bitcoin mined. Where the lines cross, the Bitcoin held is worth more than everything spent to that point. ${sellsBTC ? `About ${soldPct}% of production is sold to cover operating costs; the rest is held and appreciates at the scenario price.` : 'Mined Bitcoin is kept, not sold, and appreciates at the scenario price.'}${hasSavings ? ' The lower expense line is the same spending after write-offs, so the gap between the two is the deduction and the earlier crossing is what it buys.' : ''}`} />
          {/* Height covers the plot plus the axis strip. Too little and the tick
              labels render outside the SVG box, landing on top of the legend
              beneath it rather than being clipped where it would be noticed. */}
          <div className="report-chart" style={{ height: 218 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={roiChart} margin={{ top: 14, right: 26, left: 8, bottom: 4 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#F0F0F0" />
                <XAxis dataKey="month" tick={axisStyle} axisLine={false} tickLine={false}
                  ticks={yearTicks} interval={0} height={24} tickMargin={6}
                  tickFormatter={m => `Yr ${Math.round(m / 12)}`} />
                <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={v => `$${Math.round(v / 1000)}K`} width={48} />
                <Area type="monotone" dataKey="revenue" stroke="#92700C" fill="#92700C22" strokeWidth={2.5} name="BTC value" isAnimationActive={false} />
                <Line type="monotone" dataKey="expenses" stroke="#525252" strokeWidth={1.5} dot={false} name="Expenses" isAnimationActive={false} />
                {hasSavings && (
                  <Line type="monotone" dataKey="netExpenses" stroke="#525252" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Expenses after write-offs" isAnimationActive={false} />
                )}
                {breakEvenPoint && (
                  <ReferenceDot
                    x={breakEvenPoint.month}
                    y={breakEvenPoint.revenue}
                    r={4}
                    fill="#92700C"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                    isFront
                    label={{ value: `Break-even ${monthToDate(breakEvenShown)}`, position: 'top', offset: 10, fill: '#92700C', fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", stroke: '#FFFFFF', strokeWidth: 3, paintOrder: 'stroke' }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <Footnote style={{ margin: '4px 0 12px' }}>
            <span style={{ color: '#92700C', fontWeight: 600 }}>—</span> Cumulative BTC value&nbsp;&nbsp;
            <span style={{ color: '#525252', fontWeight: 600 }}>—</span> Cumulative expenses
            {hasSavings && <>&nbsp;&nbsp;<span style={{ color: '#525252', fontWeight: 600 }}>--</span> After write-offs</>}
          </Footnote>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, alignItems: 'start', borderTop: '1px solid #F0F0F0', paddingTop: 12 }}>
            <div>
              <Kicker color="#737373">Break-even</Kicker>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 600, color: '#92700C', marginTop: 4 }}>{monthToDate(breakEvenShown) || 'Not within the hold'}</div>
              {breakEvenShown && (
                <div style={{ fontSize: 10, color: '#737373', lineHeight: 1.6 }}>
                  <div>Month {breakEvenShown}{hasTaxOnMult ? ` · $1 in buys $${mult.toFixed(2)} of BTC` : ''}</div>
                  {breakEvenNet && !breakEvenGross
                    ? <div>Without write-offs: beyond the hold</div>
                    : breakEvenNet && breakEvenGross && breakEvenGross !== breakEvenNet
                      ? <div>Write-offs pull this in {breakEvenGross - breakEvenNet} months</div>
                      : null}
                </div>
              )}
            </div>
            <div>
              <Kicker color="#737373" style={{ marginBottom: 6 }}>This model assumes</Kicker>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, color: '#525252', lineHeight: 1.9, columns: 2 }}>
                <li>{scenarioLabel} BTC price growth</li>
                <li>${electricityCost}/kWh power, ${hostingFixed}/miner/mo hosting</li>
                <li>{btcDisposition}</li>
                <li>{fmt(totalCapExPaid)} hardware, {fmt(totalOpExOverHold)} operating</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── 05 Comparison ── */}
        {displayCalculations.length > 1 && (
          <div className="report-section print-flow" style={sec}>
            <SectionHeader kicker="Comparison" title="Same dollars, compared"
              intro={equivalentDCA
                ? `The ${fmt(equivalentDCA.totalCashDeployed)} this plan deploys, placed instead into Bitcoin or the S&P 500 on the same schedule. Mining's edge is the tax position shown above: purchases and operating costs are deductible; buying is not.`
                : 'Every visible strategy at the end of the horizon.'} />
            {/* Headline figures come from the same calculations as the table rows
                below them. They used to come from `equivalentDCA`, a second model
                of the same three quantities living in useDashboardState: it
                compounds cash forward monthly, while the engine accumulates
                shares at each month's price. Both are defensible and they
                disagreed by 11% — the card read $3.60M for the S&P while the row
                four inches below read $3.23M. One page, one instrument, two
                numbers is the kind of thing that ends a CPA's review of
                everything else. */}
            {compStats.length > 0 && <StatRow stats={compStats} />}
            <ReportTable
              columns={[
                { key: 'name', label: 'Strategy' },
                { key: 'capEx', label: 'CapEx', align: 'right' },
                { key: 'opEx', label: 'OpEx', align: 'right' },
                { key: 'btc', label: 'BTC', align: 'right' },
                { key: 'cost', label: 'Cost/BTC', align: 'right' },
                { key: 'nav', label: 'NAV', align: 'right' },
                { key: 'roi', label: 'Total ROI*', align: 'right' },
              ]}
              rows={compRows}
              footnote={`*Total ROI measures the entire position, including starting treasury appreciation and any already-owned miners. All figures assume ${scenarioLabel} BTC price growth over ${timeHorizon} years.`}
            />
          </div>
        )}

        {/* ── What this engagement is worth ── */}
        {engagementValue && engagementValue.delta > 0 && (
          <div className="report-section print-flow" style={sec}>
            <SectionHeader kicker="What this engagement is worth" title="One decision, priced"
              intro={`Hardware is quoted per machine, so the cheaper sticker looks like the better buy. Priced per unit of hashrate the two generations are almost identical. The difference is power draw, and that is paid every month for five years. Both columns below use the same budget, the same hosting rate, and the same assumptions as this plan.`} />
            <ReportTable
              columns={[
                { key: 'label', label: '' },
                { key: 'machines', label: 'Machines', align: 'right' },
                { key: 'th', label: 'Hashrate', align: 'right' },
                { key: 'kw', label: 'Power', align: 'right' },
                { key: 'opex', label: `Hosting, ${timeHorizon}yr`, align: 'right' },
                { key: 'cost', label: 'Cost / BTC', align: 'right' },
                { key: 'mult', label: 'Every $1 becomes', align: 'right' },
              ]}
              rows={[
                { label: 'This plan',
                  machines: engagementValue.mine.n,
                  th: Math.round(engagementValue.mine.th).toLocaleString() + ' TH',
                  kw: engagementValue.mine.kw.toFixed(1) + ' kW',
                  opex: fmt(engagementValue.mine.opEx),
                  cost: fmt(engagementValue.mine.cost),
                  mult: '$' + engagementValue.mine.mult.toFixed(2) },
                { label: engagementValue.alt.label,
                  machines: engagementValue.other.n,
                  th: Math.round(engagementValue.other.th).toLocaleString() + ' TH',
                  kw: engagementValue.other.kw.toFixed(1) + ' kW',
                  opex: fmt(engagementValue.other.opEx),
                  cost: fmt(engagementValue.other.cost),
                  mult: '$' + engagementValue.other.mult.toFixed(2) },
              ]}
              footnote="Same capital, same hosting rate, same price and difficulty assumptions. The only variable changed is the machine."
            />
            <StatRow stats={[
              { value: fmt(engagementValue.delta), label: 'Cost of the wrong machine', sub: `extra hosting over ${timeHorizon} years`, accent: '#1A1A1A' },
              { value: fmt(engagementValue.fee), label: 'MinerOps fee', sub: `3% of production, ${timeHorizon} years`, accent: '#92700C' },
              { value: engagementValue.ratio.toFixed(1) + 'x', label: 'Ratio', sub: 'one decision, against the whole fee', accent: '#1A1A1A' },
            ]} />
            <Footnote>
              This is the argument for the 3% and it is arithmetic, not a promise. Specification
              review is one of several decisions on this deployment where the downside is larger
              than the fee: facility selection and counterparty risk, placing hardware in service
              inside the tax year, and the notice deadlines in a hosting agreement: invoices due
              in days, production seizure when overdue, renewal windows measured in months. Those
              are managed rather than modelled, so they are not priced here.
            </Footnote>
          </div>
        )}

        <div style={{ padding: '18px 40px 32px' }}>
          <NextStep
            advisor={advisorProfile}
            disclaimer="Projections only. Results vary with Bitcoin price, network difficulty, and machine performance. ROI, tax, and dollar examples are illustrative, not guarantees, and are not financial, tax, or investment advice."
          />
        </div>
      </div>
    </div>
  );
}
